import { Mail } from "lucide-react";
import { useGoogleLogin } from "@react-oauth/google";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import fetchWithCredentials, { FetchWithCredentialsError } from "apiClient";
import { useAuth } from "context/AuthContext";
import { MOBILE_PAGE_TITLE_MENU_INSET } from "constants/pageLayout";
import { MailAddressConnectionIcon } from "components/MailAddressConnectionIcon";
import { PropertyManagerWhatsappCard, type WappAssistantMode } from "components/PropertyManagerWhatsappCard";
import { PropertyManagerDriveCard } from "components/PropertyManagerDriveCard";
import type { DriveFolderItem } from "components/PropertyManagerDriveCard";
import {
  CondoManagerWhatsappCard,
  type AutoResponseRule,
  DEFAULT_AUTO_RESPONSE_RULE,
} from "components/CondoManagerWhatsappCard";
import type { WappTemplatesStatus } from "components/WhatsAppTemplatesStatusNotice";
import {
  DisturbScheduleCard,
  normalizeTimeOffRanges,
  todayIsoDate,
  type TimeOffRange,
} from "components/DisturbScheduleCard";
import { setAssistantIdentityCache } from "hooks/useAssistantIdentity";
import {
  openGoogleDrivePicker,
  resolveParentFolders,
} from "utils/googleDrivePicker";
import {
  embeddedSignupLoginExtras,
  saveEmbeddedSignupCode,
} from "utils/embeddedSignup";

function whatsappConnectSuccessMessage(
  tr: (es: string, en: string) => string,
  stillExpiring: boolean,
): string {
  if (stillExpiring) {
    return tr(
      "WhatsApp quedó conectado, pero el token sigue por vencer. Volvé a reconectar para renovarlo.",
      "WhatsApp is connected, but the token is still expiring. Reconnect again to renew it.",
    );
  }
  return tr(
    "WhatsApp conectado correctamente.",
    "WhatsApp connected successfully.",
  );
}

type SettingsTab = "assistant" | "manager" | "integrations";

function parseSettingsTab(value: string | null): SettingsTab {
  if (value === "manager" || value === "integrations") return value;
  return "assistant";
}

function normalizeTemplatesStatus(value: unknown): WappTemplatesStatus {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (
    normalized === "approved" ||
    normalized === "pending" ||
    normalized === "rejected" ||
    normalized === "missing" ||
    normalized === "unknown"
  ) {
    return normalized;
  }
  return null;
}

function getFetchErrorMessage(error: unknown): string | null {
  if (!(error instanceof FetchWithCredentialsError)) {
    return null;
  }
  const message = error.message.replace(/^\d+:\s*/, "").trim();
  return message || null;
}

function whatsappConnectErrorMessage(
  error: unknown,
  fallback: string,
): string {
  // 409: the number is already connected elsewhere; the server explains where.
  if (error instanceof FetchWithCredentialsError && error.status === 409) {
    return getFetchErrorMessage(error) || fallback;
  }
  return fallback;
}

function mapDriveFolders(raw: unknown): DriveFolderItem[] {
  if (!Array.isArray(raw)) return [];
  const items: DriveFolderItem[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const id = typeof row.id === "number" ? row.id : Number(row.id);
    const folderId =
      typeof row.folderId === "string"
        ? row.folderId
        : typeof row.folder_id === "string"
          ? row.folder_id
          : "";
    const folderName =
      typeof row.folderName === "string"
        ? row.folderName
        : typeof row.folder_name === "string"
          ? row.folder_name
          : "";
    if (!Number.isFinite(id) || !folderId || !folderName) continue;
    const rawFileCount = row.fileCount ?? row.file_count;
    const fileCount =
      typeof rawFileCount === "number"
        ? rawFileCount
        : Number(rawFileCount) || 0;
    items.push({
      id,
      folderId,
      folderName,
      description:
        typeof row.description === "string" ? row.description : undefined,
      fileCount: Number.isFinite(fileCount) ? fileCount : 0,
    });
  }
  return items;
}

function mapDriveFilesCount(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw) && raw >= 0) {
    return raw;
  }
  const asNumber = Number(raw);
  return Number.isFinite(asNumber) && asNumber >= 0 ? asNumber : 0;
}

declare global {
  interface Window {
    FB?: {
      init: (params: Record<string, unknown>) => void;
      login: (
        callback: (response: any) => void,
        options: Record<string, unknown>,
      ) => void;
    };
    fbAsyncInit?: () => void;
  }
}

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [wappConfigId, setWappConfigId] = useState<string | null>(null);
  const [wappAppId, setWappAppId] = useState<string | null>(null);
  const [wappPhoneNumberId, setWappPhoneNumberId] = useState<string | null>(
    null,
  );
  const [wappWabaId, setWappWabaId] = useState<string | null>(null);
  const [wappBusinessId, setWappBusinessId] = useState<string | null>(null);
  const [wappPhone, setWappPhone] = useState<string | null>(null);
  const [wappTemplatesStatus, setWappTemplatesStatus] =
    useState<WappTemplatesStatus>(null);
  const [wappDisconnected, setWappDisconnected] = useState(false);
  const [wappAssistantMode, setWappAssistantMode] =
    useState<WappAssistantMode>("shared");
  const [agentName, setAgentName] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailBcc, setEmailBcc] = useState("");
  const [mailLocalPart, setMailLocalPart] = useState("");
  const [mailDomain, setMailDomain] = useState("mail.eloria.app");
  const [defaultMailAddress, setDefaultMailAddress] =
    useState("eloria@mail.eloria.app");
  const [mailAddress, setMailAddress] = useState<string | null>(null);
  const [wappConnectLoading, setWappConnectLoading] = useState(false);
  const [wappConnectMessage, setWappConnectMessage] = useState<string | null>(
    null,
  );
  const [managerWappPhoneNumberId, setManagerWappPhoneNumberId] = useState<
    string | null
  >(null);
  const [managerWappPhone, setManagerWappPhone] = useState<string | null>(null);
  const [condoManagerPhone, setCondoManagerPhone] = useState<string | null>(null);
  const [managerAutoResponseEnabled, setManagerAutoResponseEnabled] =
    useState<boolean>(false);
  const [managerAutoResponseNextEnableAt, setManagerAutoResponseNextEnableAt] =
    useState<string | null>(null);
  const [managerAutoResponseNextDisableAt, setManagerAutoResponseNextDisableAt] =
    useState<string | null>(null);
  const [managerAutoResponseRules, setManagerAutoResponseRules] = useState<
    AutoResponseRule[]
  >([]);
  const [managerDisturbRules, setManagerDisturbRules] = useState<
    AutoResponseRule[]
  >([]);
  const [managerTimeOff, setManagerTimeOff] = useState<TimeOffRange[]>([]);
  const [managerWappConnectLoading, setManagerWappConnectLoading] =
    useState<boolean>(false);
  const [managerWappConnectMessage, setManagerWappConnectMessage] = useState<
    string | null
  >(null);
  const [managerWappTokenReconnectRecommended, setManagerWappTokenReconnectRecommended] =
    useState(false);
  const [managerWappTemplatesStatus, setManagerWappTemplatesStatus] =
    useState<WappTemplatesStatus>(null);
  const [managerWappDisconnected, setManagerWappDisconnected] = useState(false);
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveEmail, setDriveEmail] = useState<string | null>(null);
  const [driveFolders, setDriveFolders] = useState<DriveFolderItem[]>([]);
  const [driveFilesCount, setDriveFilesCount] = useState(0);
  const [driveConnectLoading, setDriveConnectLoading] = useState(false);
  const [drivePickerLoading, setDrivePickerLoading] = useState(false);
  const [driveConnectMessage, setDriveConnectMessage] = useState<string | null>(
    null,
  );
  const latestEmbeddedEventRef = useRef<any>(null);
  const {
    hasPartner,
    partnerName,
    managerName,
    lang,
    refetchProfile,
    wappTokenReconnectRecommended,
  } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const tr = (es: string, en: string) => (locale === "es" ? es : en);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseSettingsTab(searchParams.get("tab"));
  const setActiveTab = useCallback(
    (tab: SettingsTab) => {
      setSearchParams(tab === "assistant" ? {} : { tab }, { replace: true });
    },
    [setSearchParams],
  );
  const sdkInitPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    async function fetchManagerWhatsappSettings() {
      try {
        const currentManager = await fetchWithCredentials("/manager-api/current-manager/");
        setCondoManagerPhone(
          typeof currentManager?.contact?.phone === "string" &&
            currentManager.contact.phone.trim()
            ? currentManager.contact.phone.trim()
            : null,
        );
        const data = await fetchWithCredentials(
          "/manager-api/manager/whatsapp/settings/",
        );
        setManagerWappPhoneNumberId(data.wappPhoneNumberId || null);
        setManagerWappPhone(
          typeof data.wappPhone === "string" && data.wappPhone.trim()
            ? data.wappPhone.trim()
            : null,
        );
        setManagerAutoResponseEnabled(Boolean(data.wappAutoResponseEnabled));
        setManagerAutoResponseNextEnableAt(data.autoResponseNextEnableAt || null);
        setManagerAutoResponseNextDisableAt(data.autoResponseNextDisableAt || null);
        const rules = Array.isArray(data.wappAutoResponseSchedule)
          ? (data.wappAutoResponseSchedule as AutoResponseRule[])
          : [];
        setManagerAutoResponseRules(
          rules.map((rule) => ({
            enabled: rule.enabled !== false,
            weekdays: Array.isArray(rule.weekdays)
              ? rule.weekdays.filter((d) => Number.isInteger(d))
              : [],
            start: typeof rule.start === "string" ? rule.start : "09:00",
            end: typeof rule.end === "string" ? rule.end : "18:00",
          })),
        );
        const disturbRules = Array.isArray(data.disturbSchedule)
          ? (data.disturbSchedule as AutoResponseRule[])
          : [];
        setManagerDisturbRules(
          disturbRules.map((rule) => ({
            enabled: rule.enabled !== false,
            weekdays: Array.isArray(rule.weekdays)
              ? rule.weekdays.filter((d) => Number.isInteger(d))
              : [],
            start: typeof rule.start === "string" ? rule.start : "09:00",
            end: typeof rule.end === "string" ? rule.end : "18:00",
          })),
        );
        setManagerTimeOff(normalizeTimeOffRanges(data.timeOff));
        setManagerWappTokenReconnectRecommended(
          Boolean(data.wappTokenReconnectRecommended),
        );
        setManagerWappTemplatesStatus(
          normalizeTemplatesStatus(data.wappTemplatesStatus),
        );
        setManagerWappDisconnected(Boolean(data.wappDisconnected));
      } catch (err) {
        console.error("Error loading manager whatsapp settings:", err);
      }
    }

    async function fetchSettings() {
      try {
        const data = await fetchWithCredentials(
          `/manager-api/property-manager/settings/`,
        );
        setWappConfigId(data.wappEmbeddedConfigId || null);
        setWappPhoneNumberId(data.wappPhoneNumberId || null);
        setWappPhone(
          typeof data.wappPhone === "string" && data.wappPhone.trim()
            ? data.wappPhone.trim()
            : null,
        );
        setWappTemplatesStatus(
          normalizeTemplatesStatus(data.wappTemplatesStatus),
        );
        setWappDisconnected(Boolean(data.wappDisconnected));
        setWappAssistantMode(
          data.wappAssistantMode === "exclusive" ? "exclusive" : "shared",
        );
        setAgentName(
          typeof data.agentName === "string" && data.agentName.trim()
            ? data.agentName.trim()
            : "Eloria",
        );
        setEmailCc(
          typeof data.emailCc === "string" ? data.emailCc.trim() : "",
        );
        setEmailBcc(
          typeof data.emailBcc === "string" ? data.emailBcc.trim() : "",
        );
        setMailLocalPart(
          typeof data.mailLocalPart === "string" ? data.mailLocalPart.trim() : "",
        );
        setMailDomain(
          typeof data.mailDomain === "string" && data.mailDomain.trim()
            ? data.mailDomain.trim()
            : "mail.eloria.app",
        );
        setDefaultMailAddress(
          typeof data.defaultMailAddress === "string" &&
            data.defaultMailAddress.trim()
            ? data.defaultMailAddress.trim()
            : "eloria@mail.eloria.app",
        );
        setMailAddress(
          typeof data.mailAddress === "string" && data.mailAddress.trim()
            ? data.mailAddress.trim()
            : null,
        );
        setWappWabaId(data.wappWabaId || null);
        setWappBusinessId(data.wappBusinessId || null);
        setWappAppId(data.wappAppId || null);
        setDriveConnected(Boolean(data.driveConnected));
        setDriveEmail(
          typeof data.driveEmail === "string" && data.driveEmail.trim()
            ? data.driveEmail.trim()
            : null,
        );
        setDriveFolders(mapDriveFolders(data.driveFolders));
        setDriveFilesCount(mapDriveFilesCount(data.driveFilesCount));
        await fetchManagerWhatsappSettings();
        setAssistantIdentityCache(data);
      } catch (err) {
        console.error("Error loading property manager settings:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchSettings();
  }, []);

  const ensureFacebookSdkLoaded = useCallback(async (appId: string) => {
    if (sdkInitPromiseRef.current) {
      await sdkInitPromiseRef.current;
      return;
    }

    sdkInitPromiseRef.current = new Promise<void>((resolve, reject) => {
      const initAndResolve = () => {
        try {
          window.FB?.init({
            appId,
            autoLogAppEvents: true,
            xfbml: true,
            version: "v25.0",
          });
          resolve();
        } catch (error) {
          reject(error);
        }
      };

      window.fbAsyncInit = initAndResolve;

      const scriptId = "facebook-jssdk";
      const existingScript = document.getElementById(
        scriptId,
      ) as HTMLScriptElement | null;

      if (window.FB) {
        initAndResolve();
        return;
      }

      if (existingScript) {
        existingScript.addEventListener("error", () =>
          reject(new Error("SDK_LOAD_FAILED")),
        );
        const onReady = () => {
          if (window.FB) initAndResolve();
        };
        existingScript.addEventListener("load", onReady, { once: true });
        setTimeout(onReady, 0);
        return;
      }

      const script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.onerror = () => reject(new Error("SDK_LOAD_FAILED"));
      document.body.appendChild(script);
    });

    await sdkInitPromiseRef.current;
  }, []);

  useEffect(() => {
    if (!wappConfigId || !wappAppId) return;
    ensureFacebookSdkLoaded(wappAppId).catch(() => {});
  }, [wappConfigId, wappAppId, ensureFacebookSdkLoaded]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!event.origin.endsWith("facebook.com")) return;
      try {
        const data = JSON.parse(String(event.data));
        if (data.type !== "WA_EMBEDDED_SIGNUP") return;
        latestEmbeddedEventRef.current = data;
        // Do not PATCH here: the OAuth code only exists in FB.login's callback.
        // A PATCH without wapp_embedded_signup_code would omit it from DRF validated_data on partial update.
      } catch {
        // Ignore non-JSON events sent by FB SDK internals.
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const patchManagerWhatsappSettings = useCallback(async () => {
    const parsedRules: AutoResponseRule[] = managerAutoResponseRules.map(
      (rule) => ({
        enabled: rule.enabled !== false,
        weekdays: Array.isArray(rule.weekdays)
          ? Array.from(new Set(rule.weekdays)).sort((a, b) => a - b)
          : [],
        start: rule.start || "09:00",
        end: rule.end || "18:00",
      }),
    );
    const parsedDisturbRules: AutoResponseRule[] = managerDisturbRules.map(
      (rule) => ({
        enabled: rule.enabled !== false,
        weekdays: Array.isArray(rule.weekdays)
          ? Array.from(new Set(rule.weekdays)).sort((a, b) => a - b)
          : [],
        start: rule.start || "09:00",
        end: rule.end || "18:00",
      }),
    );
    const parsedTimeOff = managerTimeOff.map((range) => ({
      enabled: range.enabled !== false,
      start: range.start,
      end: range.end < range.start ? range.start : range.end,
    }));
    const updated = await fetchWithCredentials(`/manager-api/manager/whatsapp/settings/`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        wapp_auto_response_enabled: managerAutoResponseEnabled,
        wapp_auto_response_schedule: parsedRules,
        disturb_schedule: parsedDisturbRules,
        time_off: parsedTimeOff,
      }),
    });
    setManagerAutoResponseNextEnableAt(updated?.autoResponseNextEnableAt || null);
    setManagerAutoResponseNextDisableAt(updated?.autoResponseNextDisableAt || null);
  }, [
    managerAutoResponseEnabled,
    managerAutoResponseRules,
    managerDisturbRules,
    managerTimeOff,
  ]);

  const patchPropertyManagerAgentName = useCallback(async () => {
    const updated = await fetchWithCredentials(
      `/manager-api/property-manager/settings/`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          agent_name: agentName.trim() || "Eloria",
        }),
      },
    );
    setAssistantIdentityCache(updated);
  }, [agentName]);

  const patchPropertyManagerWappAssistantMode = useCallback(
    async (mode: WappAssistantMode) => {
      const updated = await fetchWithCredentials(
        `/manager-api/property-manager/settings/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            wapp_assistant_mode: mode,
          }),
        },
      );
      setWappAssistantMode(
        updated?.wappAssistantMode === "exclusive" ? "exclusive" : "shared",
      );
      setAssistantIdentityCache(updated);
    },
    [],
  );

  const patchPropertyManagerMailAddress = useCallback(async () => {
    const updated = await fetchWithCredentials(
      `/manager-api/property-manager/settings/`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mail_local_part: mailLocalPart.trim() || null,
        }),
      },
    );
    setMailLocalPart(
      typeof updated?.mailLocalPart === "string" ? updated.mailLocalPart.trim() : "",
    );
    setMailAddress(
      typeof updated?.mailAddress === "string" && updated.mailAddress.trim()
        ? updated.mailAddress.trim()
        : null,
    );
    if (typeof updated?.mailDomain === "string" && updated.mailDomain.trim()) {
      setMailDomain(updated.mailDomain.trim());
    }
    if (
      typeof updated?.defaultMailAddress === "string" &&
      updated.defaultMailAddress.trim()
    ) {
      setDefaultMailAddress(updated.defaultMailAddress.trim());
    }
    setAssistantIdentityCache(updated);
  }, [mailLocalPart]);

  const patchPropertyManagerEmailRecipients = useCallback(async () => {
    const updated = await fetchWithCredentials(
      `/manager-api/property-manager/settings/`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email_cc: emailCc.trim() || null,
          email_bcc: emailBcc.trim() || null,
        }),
      },
    );
    if (typeof updated?.emailCc === "string") {
      setEmailCc(updated.emailCc.trim());
    } else {
      setEmailCc("");
    }
    if (typeof updated?.emailBcc === "string") {
      setEmailBcc(updated.emailBcc.trim());
    } else {
      setEmailBcc("");
    }
    setAssistantIdentityCache(updated);
  }, [emailCc, emailBcc]);

  /** Tras `loading`, los efectos de auto-guardado pueden ejecutarse (orden de effects: primero este). */
  const settingsHydratedRef = useRef(false);
  const skipInitialAgentNameSaveRef = useRef(true);
  const skipInitialEmailRecipientsSaveRef = useRef(true);
  const skipInitialMailAddressSaveRef = useRef(true);
  const skipInitialManagerWhatsappSaveRef = useRef(true);
  useEffect(() => {
    settingsHydratedRef.current = !loading;
    if (loading) {
      skipInitialAgentNameSaveRef.current = true;
      skipInitialEmailRecipientsSaveRef.current = true;
      skipInitialMailAddressSaveRef.current = true;
      skipInitialManagerWhatsappSaveRef.current = true;
    }
  }, [loading]);

  useEffect(() => {
    if (loading || !settingsHydratedRef.current) {
      return;
    }
    const id = window.setTimeout(() => {
      if (skipInitialAgentNameSaveRef.current) {
        skipInitialAgentNameSaveRef.current = false;
        return;
      }
      void patchPropertyManagerAgentName().catch((err) =>
        console.error("Error auto-saving assistant name:", err),
      );
    }, 500);
    return () => window.clearTimeout(id);
  }, [loading, agentName, patchPropertyManagerAgentName]);

  useEffect(() => {
    if (loading || !settingsHydratedRef.current) {
      return;
    }
    const id = window.setTimeout(() => {
      if (skipInitialEmailRecipientsSaveRef.current) {
        skipInitialEmailRecipientsSaveRef.current = false;
        return;
      }
      void patchPropertyManagerEmailRecipients().catch((err) =>
        console.error("Error auto-saving email CC/BCC settings:", err),
      );
    }, 500);
    return () => window.clearTimeout(id);
  }, [loading, emailCc, emailBcc, patchPropertyManagerEmailRecipients]);

  useEffect(() => {
    if (loading || !settingsHydratedRef.current) {
      return;
    }
    const id = window.setTimeout(() => {
      if (skipInitialMailAddressSaveRef.current) {
        skipInitialMailAddressSaveRef.current = false;
        return;
      }
      void patchPropertyManagerMailAddress().catch((err) =>
        console.error("Error auto-saving mail address:", err),
      );
    }, 500);
    return () => window.clearTimeout(id);
  }, [loading, mailLocalPart, patchPropertyManagerMailAddress]);

  useEffect(() => {
    if (loading || !settingsHydratedRef.current) {
      return;
    }
    const id = window.setTimeout(() => {
      if (skipInitialManagerWhatsappSaveRef.current) {
        skipInitialManagerWhatsappSaveRef.current = false;
        return;
      }
      void patchManagerWhatsappSettings().catch((err) =>
        console.error("Error auto-saving manager WhatsApp settings:", err),
      );
    }, 500);
    return () => window.clearTimeout(id);
  }, [
    loading,
    managerAutoResponseEnabled,
    managerAutoResponseRules,
    managerDisturbRules,
    managerTimeOff,
    patchManagerWhatsappSettings,
  ]);

  const addAutoResponseRule = () => {
    setManagerAutoResponseRules((prev) => [
      ...prev,
      { ...DEFAULT_AUTO_RESPONSE_RULE },
    ]);
  };

  const removeAutoResponseRule = (index: number) => {
    setManagerAutoResponseRules((prev) => prev.filter((_, i) => i !== index));
  };

  const updateAutoResponseRule = (
    index: number,
    patch: Partial<AutoResponseRule>,
  ) => {
    setManagerAutoResponseRules((prev) =>
      prev.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
    );
  };

  const toggleRuleWeekday = (index: number, weekday: number) => {
    setManagerAutoResponseRules((prev) =>
      prev.map((rule, i) => {
        if (i !== index) return rule;
        const weekdays = Array.isArray(rule.weekdays) ? rule.weekdays : [];
        const exists = weekdays.includes(weekday);
        const nextWeekdays = exists
          ? weekdays.filter((d) => d !== weekday)
          : [...weekdays, weekday];
        return {
          ...rule,
          weekdays: nextWeekdays.sort((a, b) => a - b),
        };
      }),
    );
  };

  const addDisturbRule = () => {
    setManagerDisturbRules((prev) => [...prev, { ...DEFAULT_AUTO_RESPONSE_RULE }]);
  };

  const removeDisturbRule = (index: number) => {
    setManagerDisturbRules((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDisturbRule = (
    index: number,
    patch: Partial<AutoResponseRule>,
  ) => {
    setManagerDisturbRules((prev) =>
      prev.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
    );
  };

  const toggleDisturbRuleWeekday = (index: number, weekday: number) => {
    setManagerDisturbRules((prev) =>
      prev.map((rule, i) => {
        if (i !== index) return rule;
        const weekdays = Array.isArray(rule.weekdays) ? rule.weekdays : [];
        const exists = weekdays.includes(weekday);
        const nextWeekdays = exists
          ? weekdays.filter((d) => d !== weekday)
          : [...weekdays, weekday];
        return {
          ...rule,
          weekdays: nextWeekdays.sort((a, b) => a - b),
        };
      }),
    );
  };

  const addTimeOff = () => {
    const today = todayIsoDate();
    setManagerTimeOff((prev) => [
      ...prev,
      { enabled: true, start: today, end: today },
    ]);
  };

  const removeTimeOff = (index: number) => {
    setManagerTimeOff((prev) => prev.filter((_, i) => i !== index));
  };

  const updateTimeOff = (index: number, patch: Partial<TimeOffRange>) => {
    setManagerTimeOff((prev) =>
      prev.map((range, i) => {
        if (i !== index) return range;
        const next = { ...range, ...patch };
        if (next.end < next.start) {
          if (patch.start !== undefined && patch.end === undefined) {
            next.end = next.start;
          } else if (patch.end !== undefined && patch.start === undefined) {
            next.start = next.end;
          }
        }
        return next;
      }),
    );
  };

  const launchManagerWhatsAppSignup = async () => {
    if (!wappConfigId || !wappAppId) {
      setManagerWappConnectMessage(
        tr(
          "Falta configuración de WhatsApp Embedded Signup.",
          "WhatsApp Embedded Signup configuration is missing.",
        ),
      );
      return;
    }
    setManagerWappConnectLoading(true);
    setManagerWappConnectMessage(null);
    latestEmbeddedEventRef.current = null;
    try {
      await ensureFacebookSdkLoaded(wappAppId);
    } catch {
      setManagerWappConnectLoading(false);
      setManagerWappConnectMessage(
        tr(
          "No se pudo cargar el SDK de Facebook.",
          "Could not load Facebook SDK.",
        ),
      );
      return;
    }

    window.FB?.login(
      (response: any) => {
        void (async () => {
          try {
            if (!response?.authResponse?.code) {
              setManagerWappConnectMessage(
                tr(
                  "No se recibió el código de autorización.",
                  "No authorization code received.",
                ),
              );
              return;
            }

            const code = response.authResponse.code;
            const { updated } = await saveEmbeddedSignupCode({
              code,
              getLatestEvent: () => latestEmbeddedEventRef.current,
              patch: (payload) =>
                fetchWithCredentials(
                  "/manager-api/manager/whatsapp/settings/",
                  {
                    method: "PATCH",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                  },
                ),
            });
            setManagerWappPhoneNumberId(updated?.wappPhoneNumberId || null);
            setManagerWappPhone(updated?.wappPhone || null);
            setManagerWappTemplatesStatus(
              normalizeTemplatesStatus(updated?.wappTemplatesStatus) ||
                "pending",
            );
            setManagerWappDisconnected(Boolean(updated?.wappDisconnected));
            const stillExpiring = Boolean(updated?.wappTokenReconnectRecommended);
            setManagerWappTokenReconnectRecommended(stillExpiring);
            setManagerWappConnectMessage(
              stillExpiring
                ? whatsappConnectSuccessMessage(tr, true)
                : tr(
                    "Número del manager conectado correctamente.",
                    "Manager number connected successfully.",
                  ),
            );
          } catch (err) {
            console.error("Error saving manager Embedded Signup data:", err);
            setManagerWappConnectMessage(
              whatsappConnectErrorMessage(
                err,
                tr(
                  "No se pudo guardar la conexión del manager.",
                  "Could not save manager connection.",
                ),
              ),
            );
          } finally {
            setManagerWappConnectLoading(false);
          }
        })();
      },
      {
        config_id: wappConfigId,
        response_type: "code",
        override_default_response_type: true,
        extras: embeddedSignupLoginExtras,
      },
    );
  };

  const launchWhatsAppSignup = async () => {
    if (!wappConfigId) {
      setWappConnectMessage(
        tr(
          "Falta la configuración de Embedded Signup.",
          "Embedded Signup configuration is missing.",
        ),
      );
      return;
    }
    if (!wappAppId) {
      setWappConnectMessage(
        tr(
          "Falta el App ID de Facebook. Recarga la página.",
          "Facebook App ID is missing. Reload the page.",
        ),
      );
      return;
    }

    setWappConnectLoading(true);
    setWappConnectMessage(null);
    latestEmbeddedEventRef.current = null;
    try {
      await ensureFacebookSdkLoaded(wappAppId);
    } catch {
      setWappConnectLoading(false);
      setWappConnectMessage(
        tr(
          "No se pudo cargar el SDK de Facebook. Verifica tu App ID y dominio permitido.",
          "Could not load Facebook SDK. Check your App ID and allowed domain.",
        ),
      );
      return;
    }

    if (!window.FB) {
      setWappConnectLoading(false);
      setWappConnectMessage(
        tr("SDK de Facebook no disponible.", "Facebook SDK not available."),
      );
      return;
    }

    window.FB.init({
      appId: wappAppId,
      autoLogAppEvents: true,
      xfbml: true,
      version: "v25.0",
    });

    // IMPORTANT: Meta expects a non-async callback for FB.login().
    const fbLoginCallback = (response: any) => {
      void (async () => {
        try {
          if (!response?.authResponse?.code) {
            setWappConnectMessage(
              tr(
                "No se recibió el código de autorización.",
                "No authorization code received.",
              ),
            );
            setWappConnectLoading(false);
            return;
          }

          const code = response.authResponse.code;
          const { updated, event } = await saveEmbeddedSignupCode({
            code,
            getLatestEvent: () => latestEmbeddedEventRef.current,
            patch: (payload) =>
              fetchWithCredentials(`/manager-api/property-manager/settings/`, {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
              }),
          });
          const data = event?.data || {};

          setWappPhoneNumberId(
            (data.phone_number_id as string) || wappPhoneNumberId,
          );
          setWappWabaId((data.waba_id as string) || wappWabaId);
          setWappBusinessId((data.business_id as string) || wappBusinessId);
          setWappTemplatesStatus(
            normalizeTemplatesStatus(updated?.wappTemplatesStatus) || "pending",
          );
          setWappDisconnected(Boolean(updated?.wappDisconnected));
          if (
            typeof updated?.wappPhone === "string" &&
            updated.wappPhone.trim()
          ) {
            setWappPhone(updated.wappPhone.trim());
          }
          if (typeof updated?.agentName === "string") {
            setAgentName(updated.agentName.trim() || "Eloria");
          }
          setAssistantIdentityCache(updated);
          setWappConnectMessage(
            whatsappConnectSuccessMessage(
              tr,
              Boolean(updated?.wappTokenReconnectRecommended),
            ),
          );
          await refetchProfile();
        } catch (err) {
          console.error("Error saving Embedded Signup data:", err);
          setWappConnectMessage(
            whatsappConnectErrorMessage(
              err,
              tr("No se pudo guardar la conexión.", "Could not save connection."),
            ),
          );
        } finally {
          setWappConnectLoading(false);
        }
      })();
    };

    window.FB?.login(fbLoginCallback, {
      config_id: wappConfigId,
      response_type: "code",
      override_default_response_type: true,
      extras: embeddedSignupLoginExtras,
    });
  };

  const connectGoogleDrive = useGoogleLogin({
    flow: "auth-code",
    scope: "https://www.googleapis.com/auth/drive.file",
    overrideScope: true,
    onSuccess: (codeResponse) => {
      void (async () => {
        const authCode = codeResponse?.code?.trim();
        if (!authCode) {
          setDriveConnectMessage(
            tr(
              "Google no devolvió un código de autorización.",
              "Google did not return an authorization code.",
            ),
          );
          return;
        }

        setDriveConnectLoading(true);
        setDriveConnectMessage(null);
        try {
          const updated = await fetchWithCredentials(
            `/manager-api/property-manager/settings/`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                google_drive_auth_code: authCode,
                google_drive_redirect_uri: window.location.origin,
              }),
            },
          );
          setDriveConnected(Boolean(updated?.driveConnected));
          setDriveEmail(
            typeof updated?.driveEmail === "string" && updated.driveEmail.trim()
              ? updated.driveEmail.trim()
              : null,
          );
          setDriveFolders(mapDriveFolders(updated?.driveFolders));
          setDriveFilesCount(mapDriveFilesCount(updated?.driveFilesCount));
          if (!updated?.driveConnected) {
            setDriveConnectMessage(
              tr(
                "No se pudo completar la conexión con Google Drive.",
                "Could not complete the Google Drive connection.",
              ),
            );
            return;
          }
          setDriveConnectMessage(
            tr(
              "Google Drive conectado correctamente.",
              "Google Drive connected successfully.",
            ),
          );
        } catch (err) {
          console.error("Error saving Google Drive connection:", err);
          setDriveConnectMessage(
            getFetchErrorMessage(err) ||
              tr(
                "No se pudo conectar Google Drive.",
                "Could not connect Google Drive.",
              ),
          );
        } finally {
          setDriveConnectLoading(false);
        }
      })();
    },
    onError: () => {
      setDriveConnectMessage(
        tr(
          "No se pudo autorizar Google Drive.",
          "Could not authorize Google Drive.",
        ),
      );
    },
  });

  const disconnectGoogleDrive = async () => {
    setDriveConnectLoading(true);
    setDriveConnectMessage(null);
    try {
      const updated = await fetchWithCredentials(
        `/manager-api/property-manager/settings/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            disconnect_google_drive: true,
          }),
        },
      );
      setDriveConnected(Boolean(updated?.driveConnected));
      setDriveEmail(
        typeof updated?.driveEmail === "string" && updated.driveEmail.trim()
          ? updated.driveEmail.trim()
          : null,
      );
      setDriveFolders(mapDriveFolders(updated?.driveFolders));
      setDriveFilesCount(mapDriveFilesCount(updated?.driveFilesCount));
      setDriveConnectMessage(
        tr("Google Drive desconectado.", "Google Drive disconnected."),
      );
    } catch (err) {
      console.error("Error disconnecting Google Drive:", err);
      setDriveConnectMessage(
        getFetchErrorMessage(err) ||
          tr(
            "No se pudo desconectar Google Drive.",
            "Could not disconnect Google Drive.",
          ),
      );
    } finally {
      setDriveConnectLoading(false);
    }
  };

  const applyDriveSettings = (updated: Record<string, unknown> | null | undefined) => {
    setDriveConnected(Boolean(updated?.driveConnected));
    setDriveEmail(
      typeof updated?.driveEmail === "string" && updated.driveEmail.trim()
        ? updated.driveEmail.trim()
        : null,
    );
    setDriveFolders(mapDriveFolders(updated?.driveFolders));
    setDriveFilesCount(mapDriveFilesCount(updated?.driveFilesCount));
  };

  const pickDriveItems = async () => {
    setDrivePickerLoading(true);
    setDriveConnectMessage(null);
    try {
      const config = await fetchWithCredentials(
        `/manager-api/property-manager/drive-picker-config/`,
      );
      if (
        !config?.accessToken ||
        !config?.apiKey ||
        !config?.clientId ||
        !config?.appId
      ) {
        throw new Error("Incomplete Google Picker config");
      }

      const picked = await openGoogleDrivePicker(
        {
          accessToken: config.accessToken,
          apiKey: config.apiKey,
          clientId: config.clientId,
          appId: String(config.appId),
        },
        {
          title: tr(
            "Elegí carpetas de Drive o los archivos de adentro",
            "Pick Drive folders or the files inside them",
          ),
        },
      );
      if (picked.length === 0) {
        return;
      }

      const foldersToAdd = picked
        .filter((item) => item.isFolder)
        .map((item) => ({
          folder_id: item.id,
          folder_name: item.name,
          description: "",
        }));
      const folderIds = new Set(foldersToAdd.map((folder) => folder.folder_id));
      const filesWithoutFolderPick = picked.filter((item) => !item.isFolder);
      const parentIdsToResolve = filesWithoutFolderPick
        .map((item) => item.parentId?.trim() || "")
        .filter((parentId) => parentId && !folderIds.has(parentId));
      if (parentIdsToResolve.length > 0) {
        const inferred = await resolveParentFolders(
          config.accessToken,
          parentIdsToResolve,
          tr("Carpeta compartida", "Shared folder"),
        );
        for (const folder of inferred) {
          if (folderIds.has(folder.folderId)) continue;
          folderIds.add(folder.folderId);
          foldersToAdd.push({
            folder_id: folder.folderId,
            folder_name: folder.folderName,
            description: "",
          });
        }
      }
      const soleFolderId =
        foldersToAdd.length === 1 ? foldersToAdd[0].folder_id : "";
      const filesToAdd = filesWithoutFolderPick.map((item) => {
        const parentFromPicker = item.parentId?.trim() || "";
        // Only attribute to a folder we are registering/sharing; otherwise
        // fall back to the sole selected folder (or blank).
        const parent_folder_id =
          parentFromPicker && folderIds.has(parentFromPicker)
            ? parentFromPicker
            : soleFolderId;
        return {
          file_id: item.id,
          file_name: item.name,
          mime_type: item.mimeType,
          description: "",
          parent_folder_id,
        };
      });

      const updated = await fetchWithCredentials(
        `/manager-api/property-manager/settings/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            drive_folders_to_add: foldersToAdd,
            drive_files_to_add: filesToAdd,
          }),
        },
      );
      applyDriveSettings(updated);
      setDriveConnectMessage(
        tr(
          "Selección de Drive guardada.",
          "Drive selection saved.",
        ),
      );
    } catch (err) {
      console.error("Error picking Google Drive items:", err);
      setDriveConnectMessage(
        getFetchErrorMessage(err) ||
          tr(
            "No se pudo abrir el selector de Google Drive.",
            "Could not open the Google Drive picker.",
          ),
      );
    } finally {
      setDrivePickerLoading(false);
    }
  };

  const removeDriveFolder = async (id: number) => {
    setDrivePickerLoading(true);
    setDriveConnectMessage(null);
    try {
      const updated = await fetchWithCredentials(
        `/manager-api/property-manager/settings/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            drive_folder_ids_to_remove: [id],
          }),
        },
      );
      applyDriveSettings(updated);
    } catch (err) {
      console.error("Error removing Drive folder:", err);
      setDriveConnectMessage(
        getFetchErrorMessage(err) ||
          tr("No se pudo quitar la carpeta.", "Could not remove the folder."),
      );
    } finally {
      setDrivePickerLoading(false);
    }
  };

  // TODO: check if the user has reached their plan limit
  const showUpgradeBanner = false;
  const assistantDisplayName = agentName.trim() || "Eloria";

  return (
    <div className="w-full h-full max-w-7xl overflow-y-auto bg-white py-4 max-lg:pl-0 max-lg:pr-4 lg:px-6">
      <h1
        className={`mb-6 text-2xl font-bold text-gray-900 ${MOBILE_PAGE_TITLE_MENU_INSET}`}
      >
        {locale === "es" ? "Configuración" : "Settings"}
      </h1>
      {loading ? (
        <p className="text-xs text-gray-500 max-lg:pl-4 lg:pl-0 lg:text-sm">
          {locale === "es"
            ? "Cargando configuración..."
            : "Loading settings..."}
        </p>
      ) : (
        <div className="max-lg:pl-4 lg:pl-0 space-y-6 lg:space-y-8">
          {showUpgradeBanner && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 lg:p-4">
              <p className="text-xs text-yellow-700 lg:text-sm">
                {hasPartner
                  ? locale === "es"
                    ? `Has alcanzado tu límite de estudiantes. Contacta a ${partnerName} para actualizar tu plan.`
                    : `You've reached your student limit. Contact ${partnerName} to update your plan.`
                  : locale === "es"
                    ? `Has alcanzado tu límite de estudiantes. Selecciona un plan a continuación para agregar más estudiantes.`
                    : `You've reached your student limit. Choose a plan below to add more students.`}
              </p>
            </div>
          )}

          <div
            className="-mx-1 flex overflow-x-auto border-b border-gray-200"
            role="tablist"
            aria-label={tr("Secciones de configuración", "Settings sections")}
          >
            {(
              [
                ["assistant", tr("Asistente", "Assistant")],
                ["manager", tr("Administrador", "Manager")],
                ["integrations", tr("Integraciones", "Integrations")],
              ] as const
            ).map(([tab, label]) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={activeTab === tab}
                onClick={() => setActiveTab(tab)}
                className={`shrink-0 px-4 py-3 text-sm font-medium transition ${
                  activeTab === tab
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="space-y-3 lg:space-y-4" role="tabpanel">
            {activeTab === "assistant" ? (
              <>
                <div className="space-y-2">
                  <label
                    htmlFor="agent-name"
                    className="block text-xs lg:text-sm font-medium text-gray-700"
                  >
                    {tr("Nombre del asistente", "Assistant name")}
                  </label>
                  <input
                    id="agent-name"
                    type="text"
                    maxLength={100}
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                    placeholder={tr("Ej: Sofi", "Ex: Sofi")}
                    className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>

                <PropertyManagerWhatsappCard
                  variant="settings"
                  tr={tr}
                  assistantDisplayName={assistantDisplayName}
                  wappPhoneNumberId={wappPhoneNumberId}
                  wappPhone={wappPhone}
                  wappAssistantMode={wappAssistantMode}
                  onWappAssistantModeChange={(mode) => {
                    setWappAssistantMode(mode);
                    void patchPropertyManagerWappAssistantMode(mode).catch(
                      (err) =>
                        console.error(
                          "Error saving assistant WhatsApp mode:",
                          err,
                        ),
                    );
                  }}
                  wappAppId={wappAppId}
                  wappConnectLoading={wappConnectLoading}
                  wappConnectMessage={wappConnectMessage}
                  wappTokenReconnectRecommended={wappTokenReconnectRecommended}
                  wappTemplatesStatus={wappTemplatesStatus}
                  wappDisconnected={wappDisconnected}
                  onLaunchSignup={launchWhatsAppSignup}
                />

                <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-3.5 sm:p-4 space-y-2">
              <div className="flex min-w-0 items-center gap-2.5">
                <Mail
                  className="h-5 w-5 shrink-0 text-gray-600"
                  strokeWidth={2}
                  aria-hidden
                />
                <h2 className="text-base font-semibold tracking-tight text-gray-900">
                  {tr("Correo electrónico", "Email")}
                </h2>
              </div>
              <p className="text-xs leading-snug text-gray-600">
                {tr(
                  `Configura la dirección de correo de ${assistantDisplayName} para tu empresa y las copias opcionales al enviar correos.`,
                  `Set ${assistantDisplayName}'s mail address for your company and optional copies when sending email.`,
                )}
              </p>
              <div className="space-y-1">
                <label
                  htmlFor="mail-local-part"
                  className="block text-[11px] leading-snug text-gray-600"
                >
                  <span className="font-medium">
                    {tr(
                      `Dirección de ${assistantDisplayName}`,
                      `${assistantDisplayName} address`,
                    )}
                  </span>
                </label>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <div className="flex w-full max-w-xs shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-gray-300 sm:w-auto">
                    <input
                      id="mail-local-part"
                      type="text"
                      value={mailLocalPart}
                      onChange={(e) =>
                        setMailLocalPart(
                          e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                        )
                      }
                      placeholder={tr("tu-empresa", "your-company")}
                      className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                    />
                    <span className="shrink-0 text-sm text-gray-500">
                      @{mailDomain}
                    </span>
                  </div>
                  <div className="flex min-w-0 items-center gap-2.5">
                    <MailAddressConnectionIcon connected={Boolean(mailAddress)} />
                    {mailAddress ? (
                      <p className="min-w-0 text-xs text-gray-600">
                        {tr("Activa:", "Active:")}{" "}
                        <span className="font-medium text-gray-900">
                          {mailAddress}
                        </span>
                      </p>
                    ) : (
                      <p className="min-w-0 text-xs text-gray-500">
                        {tr(
                          "Sin dirección personalizada, se usa",
                          "Without a custom address,",
                        )}{" "}
                        <span className="font-medium text-gray-900">
                          {defaultMailAddress}
                        </span>
                        .
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-xs leading-snug text-gray-600">
                {tr(
                  `Cuando ${assistantDisplayName} envía un correo a un contacto, también copia estas direcciones.`,
                  `When ${assistantDisplayName} emails a contact, it also copies these addresses.`,
                )}
              </p>
              <div className="grid gap-2 sm:grid-cols-2 sm:gap-3">
                <div className="space-y-1">
                  <label
                    htmlFor="email-cc"
                    className="block text-[11px] leading-snug text-gray-600"
                  >
                    <span className="font-medium">CC</span>
                    <span className="font-normal text-gray-500">
                      {" "}
                      —{" "}
                      {tr(
                        "copia visible para todos en el hilo",
                        "visible to everyone on the thread",
                      )}
                    </span>
                  </label>
                  <input
                    id="email-cc"
                    type="text"
                    value={emailCc}
                    onChange={(e) => setEmailCc(e.target.value)}
                    placeholder={tr(
                      "admin@empresa.com",
                      "admin@company.com",
                    )}
                    className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>
                <div className="space-y-1">
                  <label
                    htmlFor="email-bcc"
                    className="block text-[11px] leading-snug text-gray-600"
                  >
                    <span className="font-medium">{tr("CCO", "BCC")}</span>
                    <span className="font-normal text-gray-500">
                      {" "}
                      —{" "}
                      {tr(
                        "copia oculta al destinatario principal",
                        "hidden from the main recipient",
                      )}
                    </span>
                  </label>
                  <input
                    id="email-bcc"
                    type="text"
                    value={emailBcc}
                    onChange={(e) => setEmailBcc(e.target.value)}
                    placeholder={tr(
                      "archivo@empresa.com",
                      "archive@company.com",
                    )}
                    className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>
              </div>
                </div>
              </>
            ) : null}

            {activeTab === "manager" ? (
              <>
                <CondoManagerWhatsappCard
                  tr={tr}
                  locale={locale}
                  condoManagerName={
                    managerName?.trim() ||
                    (locale === "es" ? "tu administrador" : "your manager")
                  }
                  condoManagerPhone={condoManagerPhone}
                  managerWappPhoneNumberId={managerWappPhoneNumberId}
                  managerWappPhone={managerWappPhone}
                  managerWappConnectLoading={managerWappConnectLoading}
                  managerWappConnectMessage={managerWappConnectMessage}
                  managerWappTokenReconnectRecommended={
                    managerWappTokenReconnectRecommended
                  }
                  managerWappTemplatesStatus={managerWappTemplatesStatus}
                  managerWappDisconnected={managerWappDisconnected}
                  companyWappPhone={wappPhoneNumberId ? wappPhone : null}
                  onLaunchManagerSignup={launchManagerWhatsAppSignup}
                  managerAutoResponseEnabled={managerAutoResponseEnabled}
                  managerAutoResponseNextEnableAt={managerAutoResponseNextEnableAt}
                  managerAutoResponseNextDisableAt={
                    managerAutoResponseNextDisableAt
                  }
                  onManagerAutoResponseEnabledChange={setManagerAutoResponseEnabled}
                  managerAutoResponseRules={managerAutoResponseRules}
                  onAddAutoResponseRule={addAutoResponseRule}
                  onRemoveAutoResponseRule={removeAutoResponseRule}
                  onUpdateAutoResponseRule={updateAutoResponseRule}
                  onToggleRuleWeekday={toggleRuleWeekday}
                />

                <DisturbScheduleCard
                  tr={tr}
                  locale={locale}
                  title={tr(
                    "Horario en que puedes ser contactado",
                    "Hours when you can be contacted",
                  )}
                  description={tr(
                    "Fuera de estos horarios, Eloria no te enviará mensajes salvo que sean urgentes.",
                    "Outside these hours, Eloria will not message you unless it is urgent.",
                  )}
                  rules={managerDisturbRules}
                  onAddRule={addDisturbRule}
                  onRemoveRule={removeDisturbRule}
                  onUpdateRule={updateDisturbRule}
                  onToggleRuleWeekday={toggleDisturbRuleWeekday}
                  timeOff={managerTimeOff}
                  onAddTimeOff={addTimeOff}
                  onRemoveTimeOff={removeTimeOff}
                  onUpdateTimeOff={updateTimeOff}
                />
              </>
            ) : null}

            {activeTab === "integrations" ? (
              <PropertyManagerDriveCard
                tr={tr}
                driveConnected={driveConnected}
                driveEmail={driveEmail}
                driveFolders={driveFolders}
                driveFilesCount={driveFilesCount}
                driveConnectLoading={driveConnectLoading}
                driveConnectMessage={driveConnectMessage}
                drivePickerLoading={drivePickerLoading}
                onConnect={() => connectGoogleDrive()}
                onDisconnect={() => void disconnectGoogleDrive()}
                onPickItems={() => void pickDriveItems()}
                onRemoveFolder={(id) => void removeDriveFolder(id)}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
